import { useState, type ReactNode } from 'react';
import { useStore, isPremium } from '../store/useStore';
import { manageSubscriptionsUrl, storeName } from '../billing/storeLabels';
import { APPLE_REVOKE_HELP_URL, CONFIRM_WORD, isConfirmed } from '../sync/accountDeletion';

/**
 * Settings → Data → "Delete my account" (13 Oct 2026, Apple
 * 5.1.1(v)). In the app, no email, one typed confirmation. Before the user
 * confirms it says exactly what goes, that an early-tester lifetime grant
 * goes with the account, that store subscriptions keep billing until they
 * are cancelled in the store, and — for Sign in with Apple — how to remove
 * ZoneIn from their Apple ID (Apple TN3194: ZoneIn holds no Apple tokens).
 * The outcome appears under the button (CLAUDE.md §2). Draws its own
 * divider, so a signed-out card shows nothing at all.
 */
function Section({ children }: { children: ReactNode }) {
  return <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">{children}</div>;
}

export function DeleteAccountPanel() {
  const syncStatus = useStore((s) => s.syncStatus);
  const syncEmail = useStore((s) => s.syncEmail);
  const syncProviders = useStore((s) => s.syncProviders);
  const subscription = useStore((s) => s.subscription);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [state, setState] = useState<'idle' | 'working' | 'deleted' | 'error'>('idle');
  const [message, setMessage] = useState('');
  // Captured when the panel opens: after deletion the store forgets providers.
  const [usedApple, setUsedApple] = useState(false);

  if (state === 'deleted') {
    return (
      <Section>
        <div
          role="status"
          className="rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-950/20 p-3 text-[11px] leading-relaxed text-emerald-800 dark:text-emerald-300"
        >
          <p className="font-bold">✓ Your ZoneIn account has been deleted.</p>
          <p className="mt-1">
            Its cloud data, backups and sign-in are gone, and this device has been cleared. You can keep using
            ZoneIn without an account.
          </p>
          {usedApple && (
            <p className="mt-1">
              Last step for Sign in with Apple: on your iPhone, Settings → your name → Sign in with Apple → ZoneIn
              AI → Stop Using.{' '}
              <a href={APPLE_REVOKE_HELP_URL} target="_blank" rel="noreferrer" className="font-bold underline">
                Apple's instructions
              </a>
            </p>
          )}
        </div>
      </Section>
    );
  }
  if (syncStatus !== 'syncing') return null;

  if (!open) {
    return (
      <Section>
        <button
          onClick={() => {
            setUsedApple((syncProviders ?? []).includes('apple.com'));
            setOpen(true);
          }}
          className="text-[11px] font-bold text-red-600 dark:text-red-400 underline-offset-2 hover:underline"
        >
          Delete my account…
        </button>
      </Section>
    );
  }

  const premium = isPremium(subscription);
  const recurring = premium && !subscription.alphaTester && subscription.plan !== 'lifetime';
  const run = async () => {
    if (!isConfirmed(typed) || state === 'working') return;
    setState('working');
    setMessage('');
    const m = await import('../sync/firebaseSync');
    const result = await m.deleteAccount();
    if (result.ok) setState('deleted');
    else {
      setState('error');
      setMessage(result.message);
    }
  };

  return (
    <Section>
      <div className="space-y-2 rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50/60 dark:bg-red-950/10 p-3 text-[11px] leading-relaxed text-slate-700 dark:text-slate-300">
        <p className="text-xs font-bold text-red-700 dark:text-red-300">Delete your ZoneIn account</p>
        <p>
          This permanently deletes the account <b>{syncEmail}</b>: your synced tasks, plans, notes, tags,
          archive, week history, Notebook pages and daily backups, your sign-in, and everything ZoneIn stores about
          it. It also clears this device. It can't be undone.
        </p>
        {subscription.alphaTester && (
          <p className="font-semibold text-red-700 dark:text-red-300">
            Your early-tester lifetime access is tied to this account and will be deleted with it.
          </p>
        )}
        {recurring && (
          <p>
            Your subscription is billed by {storeName()} and <b>keeps renewing until you cancel it there</b> —
            deleting your account does not cancel it.{' '}
            <a href={manageSubscriptionsUrl()} target="_blank" rel="noreferrer" className="font-bold underline">
              Manage subscriptions
            </a>
          </p>
        )}
        {usedApple && (
          <p>
            You signed in with Apple: afterwards, also remove ZoneIn from your Apple ID (Settings → your name →
            Sign in with Apple → ZoneIn AI → Stop Using).
          </p>
        )}
        <label className="block">
          <span className="font-semibold">Type {CONFIRM_WORD} to confirm</span>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            aria-label={`Type ${CONFIRM_WORD} to confirm`}
            className="mt-1 w-full rounded-lg border border-red-200 dark:border-red-500/30 bg-white dark:bg-slate-900 px-3 py-2 text-base sm:text-xs text-slate-900 dark:text-white focus:border-red-500 focus:outline-none"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => void run()}
            disabled={!isConfirmed(typed) || state === 'working'}
            className="rounded-lg bg-red-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-red-500 disabled:opacity-50"
          >
            {state === 'working' ? 'Deleting your account…' : 'Delete my account permanently'}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              setTyped('');
              setState('idle');
              setMessage('');
            }}
            disabled={state === 'working'}
            className="rounded-lg px-3 py-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            Cancel
          </button>
        </div>
        {state === 'error' && (
          <p role="alert" className="break-words rounded-lg bg-red-100 dark:bg-red-950/30 p-2 text-red-700 dark:text-red-300">
            {message}
          </p>
        )}
      </div>
    </Section>
  );
}
